import { state } from "../state";
import { SpineEntry } from "../types";
import { createPlayer } from "./player";
import { setActiveListItem } from "../ui/controls";
import {
  readFileAsDataURL,
  readFileAsText,
  readFileAsArrayBuffer
} from "../utils/file-utils";
import { extractVersionFromBinary, mapSkeletonVersionToRuntime, parseAtlasPageNames } from "../utils/spine-utils";
import i18next from "i18next";

export async function loadSpineEntries(): Promise<SpineEntry[]> {
  return [];
}

export async function handleLocalFiles(filesList: File[] | FileList, list: HTMLUListElement | null) {
  const files = Array.from(filesList);
  if (files.length === 0) return;

  const skel = files.find((f) => f.name.toLowerCase().endsWith(".skel"));
  const json = files.find((f) => f.name.toLowerCase().endsWith(".json"));
  const atlas = files.find((f) => f.name.toLowerCase().endsWith(".atlas"));
  const pngFiles = files.filter((f) =>
    f.name.toLowerCase().endsWith(".png")
  );

  if ((!skel && !json) || !atlas || pngFiles.length === 0) {
    alert(i18next.t("errorMissingFiles"));
    return;
  }

  const skeletonFile = skel ?? json;
  if (!skeletonFile) {
    return;
  }
  const nameBase = skeletonFile.name.replace(/\.(skel|json)$/i, "");
  const entryId = `local_${nameBase}_${Date.now()}`;

  const [skeletonData, atlasText, atlasData, skeletonBuffer] = await Promise.all(
    [
    readFileAsDataURL(skeletonFile),
    readFileAsText(atlas),
    readFileAsDataURL(atlas),
    readFileAsArrayBuffer(skeletonFile)
    ]
  );

  const rawDataURIs: Record<string, string> = {
    [skeletonFile.name]: skeletonData,
    [atlas.name]: atlasData
  };

  const pageNames = parseAtlasPageNames(atlasText);

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const versionFromBinary = extractVersionFromBinary(skeletonBuffer);
  const runtimeVersion = mapSkeletonVersionToRuntime(versionFromBinary);

  const pngPromises: Promise<void>[] = [];
  pageNames.forEach((pageName) => {
    const pngFile = pngFiles.find((f) => f.name === pageName);
    if (!pngFile) {
      console.warn(
        "Spine local load: atlas page declared but PNG not selected:",
        pageName
      );
      return;
    }
    const p = readFileAsDataURL(pngFile).then((dataUrl) => {
      rawDataURIs[pageName] = dataUrl;
    });
    pngPromises.push(p);
  });

  await Promise.all(pngPromises);

  const newEntry: SpineEntry = {
    id: entryId,
    name: nameBase,
    atlasUrl: atlas.name,
    rawDataURIs,
    sourceType: "local"
  };

  if (skel) {
    newEntry.skelUrl = skeletonFile.name;
  } else {
    newEntry.jsonUrl = skeletonFile.name;
  }

  state.spineEntries.push(newEntry);
  state.spineMetaById[entryId] = {
    id: entryId,
    sourceType: "local",
    skeletonPath: skeletonFile.name,
    atlasPath: atlas.name,
    pngPageNames: pageNames,
    atlasPageCount: pageNames.length,
    totalBytes,
    version: versionFromBinary ?? undefined,
    runtimeVersion
  };
  state.currentActiveId = newEntry.id;

  if (list) {
      list.innerHTML = "";

      state.spineEntries.forEach((entry) => {
        const item = document.createElement("li");
        item.className = "spine-item";
        item.textContent = entry.name;
        item.dataset.id = entry.id;
        if (entry.id === state.currentActiveId) {
          item.classList.add("active");
          void createPlayer(entry);
        }
        item.addEventListener("click", () => {
          if (state.currentActiveId === entry.id) return;
          state.currentActiveId = entry.id;
          setActiveListItem(entry.id);
          void createPlayer(entry);
        });
        list.appendChild(item);
      });
  } else {
      void createPlayer(newEntry);
  }
}

export async function setupFileDrop() {
  console.log("Setting up web file drop listener...");

  const preventDefaults = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
    document.addEventListener(eventName, preventDefaults, false);
  });

  const overlay = document.getElementById('drag-overlay');
  let dragCounter = 0;

  document.addEventListener('dragenter', (e) => {
      dragCounter++;
      if (overlay) overlay.style.display = 'flex';
  });

  document.addEventListener('dragleave', (e) => {
      dragCounter--;
      if (dragCounter <= 0) {
          dragCounter = 0;
          if (overlay) overlay.style.display = 'none';
      }
  });

  document.addEventListener('dragover', (e) => {
      if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'copy';
      }
  });

  document.addEventListener('drop', async (e) => {
      dragCounter = 0;
      if (overlay) overlay.style.display = 'none';

      if (!e.dataTransfer?.items) return;

      const files: File[] = [];

      const readEntry = async (entry: any): Promise<void> => {
          if (entry.isFile) {
              return new Promise((resolve) => {
                  entry.file((file: File) => {
                      files.push(file);
                      resolve();
                  });
              });
          } else if (entry.isDirectory) {
              const dirReader = entry.createReader();
              const entries = await new Promise<any[]>((resolve) => {
                  dirReader.readEntries((results: any) => resolve(results));
              });
              for (const childEntry of entries) {
                  await readEntry(childEntry);
              }
          }
      };

      const promises: Promise<void>[] = [];
      const entriesToRead: any[] = [];
      
      // Synchronously collect all root entries to avoid losing dataTransfer context
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
          const item = e.dataTransfer.items[i];
          if (item.kind === 'file') {
              const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : (item as any).getAsEntry ? (item as any).getAsEntry() : null;
              if (entry) {
                  entriesToRead.push(entry);
              } else {
                  const file = item.getAsFile();
                  if (file) files.push(file);
              }
          }
      }

      for (const entry of entriesToRead) {
          promises.push(readEntry(entry));
      }

      await Promise.all(promises);

      if (files.length > 0) {
          const list = document.getElementById("spine-list") as HTMLUListElement | null;
          await handleLocalFiles(files, list);
      }
  });
}
